'use client';

import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { SpinningLoader } from '@/app_components/icons/SpinningLoader';
import { SignInReadOnlyEmailInputV2 } from '@/app_components/SignIn/Components/SignInReadOnlyEmailInputV2';
import { SignInResetPassword } from '@/app_components/SignIn/Components/SignInResetPassword';
import { SignInTitle } from '@/app_components/SignIn/Components/SignInSharedComponents';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import { Form, FormControl, FormField, FormItem } from '@/app_components/ui/form';
import { PasswordInputV2 } from '@/app_components/ui/PasswordInputV2';
import { trackEvent } from '@/app_lib/services/Gtm/utils';
import { UNEXPECTED_ERROR_MESSAGE } from '@/app_lib/types/accounts/signin';
import { isSignInApiSuccessPassword, type SignInApiResponse } from '@/app_lib/types/accounts/signinApi';
import { getDeviceForTracking } from '@/app_lib/utils/deviceUtils';
import { clearSignInRedirectCookie, getSignInRedirectDestination } from '@/app_lib/utils/signinRedirect';
import { passwordedLoginSchema } from '@/app_lib/utils/zod.helpers';

interface SignInPasswordedFormProps {
  email: string;
  onEditEmailClick: () => void;
  setDisableSocialLogins: Dispatch<SetStateAction<boolean>>;
  disabled?: boolean;
}

export const SignInPasswordedForm = ({
  email,
  onEditEmailClick,
  setDisableSocialLogins,
  disabled,
}: SignInPasswordedFormProps) => {
  const form = useForm({
    defaultValues: { email, password: '' },
    reValidateMode: 'onSubmit',
  });

  const {
    formState: { isSubmitting, isSubmitSuccessful, isValid, errors },
    setError,
  } = form;

  useEffect(() => {
    setDisableSocialLogins(isSubmitting || isSubmitSuccessful);
  }, [isSubmitting, isSubmitSuccessful, setDisableSocialLogins]);

  const onSubmit = async ({ password }: { password: string }) => {
    const parsed = passwordedLoginSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError('password', {
        message: UNEXPECTED_ERROR_MESSAGE,
        type: 'manual',
      });
      return;
    }

    try {
      const res = await fetch('/api/signin/login', {
        body: JSON.stringify({ email: parsed.data.email, password: parsed.data.password }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });

      const response: SignInApiResponse = await res.json();

      if (isSignInApiSuccessPassword(response)) {
        trackEvent(
          {
            deviceType: getDeviceForTracking(),
            eventAction: 'login_attempt',
            loginMethod: 'Piano',
            loginSuccess: true,
            loginType: 'password',
            pianoUserId: response.pianoUid,
          },
          'signInEventTracker',
        );
        const redirectUrl = getSignInRedirectDestination();
        clearSignInRedirectCookie();
        window.location.href = redirectUrl;
        return;
      }
      trackEvent(
        {
          deviceType: getDeviceForTracking(),
          eventAction: 'login_attempt',
          loginMethod: 'Piano',
          loginSuccess: false,
          loginType: 'password',
        },
        'signInEventTracker',
      );
      const message =
        response.message === 'That combination of email and password is not recognized'
          ? 'The information you have entered does not match a registered account. Please try again.'
          : response.message || UNEXPECTED_ERROR_MESSAGE;
      setError('email', { message: '', type: 'manual' });
      setError('password', { message, type: 'manual' });
    } catch {
      trackEvent(
        {
          deviceType: getDeviceForTracking(),
          eventAction: 'login_attempt',
          loginMethod: 'Piano',
          loginSuccess: false,
          loginType: 'password',
        },
        'signInEventTracker',
      );
      setError('email', { message: '', type: 'manual' });
      setError('password', {
        message: UNEXPECTED_ERROR_MESSAGE,
        type: 'manual',
      });
    }
  };

  const clearPasswordError = () => {
    if (errors.password) {
      form.clearErrors('password');
      form.clearErrors('email');
    }
  };

  useEffect(() => {
    trackEvent(
      {
        deviceType: getDeviceForTracking(),
        eventAction: 'account_signin_step_2',
        loginType: 'password',
      },
      'signInEventTracker',
    );
  }, []);

  return (
    <div className='flex flex-col items-center justify-center layout-gap-xs w-full'>
      <SignInTitle title='Sign in to your account' />
      <Form {...form}>
        <form
          className='flex flex-col items-center justify-center layout-gap-xs w-full'
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name='email'
            render={({ field, fieldState }) => (
              <FormItem className='flex w-full flex-col space-y-0 gap-xs'>
                <FormControl>
                  <SignInReadOnlyEmailInputV2
                    hasError={Boolean(fieldState.error)}
                    onChange={field.onChange}
                    onEditEmailClick={onEditEmailClick}
                    value={email}
                  />
                </FormControl>
              </FormItem>
            )}
            rules={{
              required: 'Email is required',
            }}
          />
          <FormField
            control={form.control}
            name='password'
            render={({ field, fieldState }) => (
              <FormItem className='flex w-full flex-col space-y-0 gap-xs'>
                <FormControl>
                  <PasswordInputV2
                    autoFocus
                    error={fieldState.error?.message}
                    id='password'
                    label='Password'
                    onChange={(e) => {
                      field.onChange(e);
                      clearPasswordError();
                    }}
                    placeholder='Enter your password'
                    required
                    value={field.value}
                  />
                </FormControl>
              </FormItem>
            )}
            rules={{
              required: 'Password is required',
            }}
          />

          <ButtonV2
            as='button'
            className='w-full max-h-12'
            disabled={isSubmitSuccessful || isSubmitting || !isValid || disabled}
            noCapitalize
            size='lg'
            type='submit'
            variant='primary'
          >
            {isSubmitting || isSubmitSuccessful ? <SpinningLoader /> : 'Sign in'}
          </ButtonV2>
        </form>
      </Form>

      <SignInResetPassword
        email={email}
        onResetRequested={() => form.reset()}
      />
    </div>
  );
};
